// Opens an org's dashboard in platform view (D-5 Ring 3, audited enter).
// Shared by the header org switcher and the admin Wallets dossier.

import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { resolveCurrentUser } from '../lib/user-permissions'
import { useOrgContextStore } from '../store/orgContext'
import { postOrgContextEvent } from '../lib/orgContextAudit'
import type { DirectoryOrg } from '../mocks/data/organizations'

export type EnterableOrg = Pick<DirectoryOrg, 'side' | 'orgId' | 'orgName' | 'orgType'>

/**
 * Opens an org's dashboard in platform view (audited enter + navigate). Returns
 * null when the admin may not enter orgs at all, so callers can hide the entry.
 * Shared by the header switcher and the Wallets dossier.
 */
export function useEnterOrgView(): ((org: EnterableOrg) => void) | null {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const enterOrg = useOrgContextStore((s) => s.enterOrg)
  const me = resolveCurrentUser({ role: 'admin' })
  const canSeller = me.effective_capabilities.includes('platform.access_seller_orgs')
  const canVendor = me.effective_capabilities.includes('platform.access_vendor_orgs')
  if (!canSeller && !canVendor) return null
  const enteredWith: 'seller' | 'vendor' | 'both' =
    canSeller && canVendor ? 'both' : canSeller ? 'seller' : 'vendor'

  return (org) => {
    if (org.side === 'seller' ? !canSeller : !canVendor) return
    // Every cached answer belongs to the context being left. Without this the
    // customer's dashboard opens showing the platform's own cached shops until
    // something happens to refetch them — the same wrong-store confusion the
    // backend scoping just removed, arriving from the client instead.
    queryClient.clear()
    enterOrg(
      { side: org.side, orgId: org.orgId, orgName: org.orgName, orgType: org.orgType },
      me.user_id,
      new Date().toISOString(),
    )
    postOrgContextEvent({
      type: 'enter',
      side: org.side,
      orgId: org.orgId,
      orgName: org.orgName,
      orgType: org.orgType,
      enteredWith,
    })
    navigate({ to: org.side === 'seller' ? '/seller' : '/supply' })
  }
}
